<script>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAI } from 'dashboard/composables/useAI';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { MESSAGE_TYPE } from 'shared/constants/messages';

export default {
  name: 'AISuggestionPanel',
  components: {
    NextButton,
    Icon,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ['use-suggestion', 'dismiss'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const suggestion = ref('');
    const suggestionSource = ref(''); // 'canned', 'aidexacx', 'ai'
    const isLoading = ref(false);
    const isDismissed = ref(false);
    const matchedCannedResponse = ref(null);

    const currentChat = useMapGetter('getSelectedChat');
    const cannedResponses = useMapGetter('getCannedResponses');
    const { isAIIntegrationEnabled, processEvent } = useAI();

    // Get the last incoming message from customer
    const lastIncomingMessage = computed(() => {
      if (!currentChat.value?.messages) return null;
      const messages = currentChat.value.messages;
      // Find last message from customer (message_type === 0)
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].message_type === MESSAGE_TYPE.INCOMING) {
          return messages[i];
        }
      }
      return null;
    });

    // Check if we should show suggestion (only when last message is from customer)
    const shouldShowSuggestion = computed(() => {
      if (isDismissed.value) return false;
      if (!lastIncomingMessage.value) return false;

      const messages = currentChat.value?.messages || [];
      if (messages.length === 0) return false;

      // Check if last non-activity message is from customer
      const lastMessage = messages[messages.length - 1];
      return lastMessage.message_type === MESSAGE_TYPE.INCOMING;
    });

    // --- Cache helpers ---
    const getCachedSuggestion = () => {
      return store.getters['aiSuggestionCache/getSuggestion'](
        props.conversationId
      );
    };

    const saveSuggestionToCache = () => {
      if (!suggestion.value || !lastIncomingMessage.value) return;
      store.dispatch('aiSuggestionCache/setSuggestion', {
        conversationId: props.conversationId,
        suggestion: suggestion.value,
        suggestionSource: suggestionSource.value,
        lastMessageId: lastIncomingMessage.value.id,
      });
    };

    const restoreFromCache = cached => {
      suggestion.value = cached.suggestion;
      suggestionSource.value = cached.suggestionSource;
      matchedCannedResponse.value = null; // canned ref not persisted in cache
      isLoading.value = false;
    };

    // Search canned responses for matching content
    const findMatchingCannedResponse = query => {
      if (!query || !cannedResponses.value?.length) return null;

      const queryLower = query.toLowerCase();
      const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2);

      let bestMatch = null;
      let bestScore = 0;

      for (const canned of cannedResponses.value) {
        const shortCodeLower = (canned.short_code || '').toLowerCase();
        const contentLower = (canned.content || '').toLowerCase();

        let score = 0;

        // Check if query words appear in short_code or content
        for (const word of queryWords) {
          if (shortCodeLower.includes(word)) score += 3;
          if (contentLower.includes(word)) score += 1;
        }

        // Check for common keywords
        const keywords = [
          'fiyat',
          'price',
          'bilgi',
          'info',
          'yardım',
          'help',
          'sipariş',
          'order',
          'kargo',
          'shipping',
          'iade',
          'return',
          'ödeme',
          'payment',
          'teşekkür',
          'thanks',
          'merhaba',
          'hello',
          'nasıl',
          'how',
          'neden',
          'why',
          'ne zaman',
          'when',
        ];

        for (const keyword of keywords) {
          if (
            queryLower.includes(keyword) &&
            (shortCodeLower.includes(keyword) || contentLower.includes(keyword))
          ) {
            score += 5;
          }
        }

        if (score > bestScore && score >= 3) {
          bestScore = score;
          bestMatch = canned;
        }
      }

      return bestMatch;
    };

    // Generate suggestion based on the incoming message
    const generateSuggestion = async ({ skipCache = false } = {}) => {
      if (!lastIncomingMessage.value?.content) return;

      // Check cache first (unless explicitly skipped)
      if (!skipCache) {
        const cached = getCachedSuggestion();
        if (cached && cached.lastMessageId === lastIncomingMessage.value.id) {
          restoreFromCache(cached);
          return;
        }
      }

      isLoading.value = true;
      suggestion.value = '';
      suggestionSource.value = '';
      matchedCannedResponse.value = null;

      try {
        // First, fetch canned responses if not loaded
        if (!cannedResponses.value?.length) {
          await store.dispatch('getCannedResponse', { searchKey: '' });
        }

        const customerMessage = lastIncomingMessage.value.content;

        // Step 1: Try to find matching canned response
        const matched = findMatchingCannedResponse(customerMessage);
        if (matched) {
          suggestion.value = matched.content;
          suggestionSource.value = 'canned';
          matchedCannedResponse.value = matched;
          isLoading.value = false;
          saveSuggestionToCache();
          return;
        }

        // Step 2: If no canned response, try AI
        if (isAIIntegrationEnabled.value) {
          // Use AI to generate response
          const aiResponse = await processEvent('reply_suggestion');
          if (aiResponse) {
            suggestion.value = aiResponse;
            suggestionSource.value = 'ai';
            isLoading.value = false;
            saveSuggestionToCache();
            return;
          }
        }

        // No suggestion available
        suggestion.value = '';
      } catch (error) {
        console.error('Error generating suggestion:', error);
        suggestion.value = '';
      } finally {
        isLoading.value = false;
      }
    };

    // Handle using the suggestion
    const useSuggestion = () => {
      if (!suggestion.value) return;

      emit('use-suggestion', {
        content: suggestion.value,
        source: suggestionSource.value,
        cannedResponse: matchedCannedResponse.value,
      });

      // Log that suggestion was used
      logSuggestionUsage(true);
      isDismissed.value = true;
    };

    // Handle dismissing the suggestion
    const dismissSuggestion = () => {
      // Log that suggestion was not used
      if (suggestion.value) {
        logSuggestionUsage(false);
      }
      isDismissed.value = true;
      emit('dismiss');
    };

    // Log suggestion usage as private note
    const logSuggestionUsage = async wasUsed => {
      if (!suggestion.value || !props.conversationId) return;

      const truncatedSuggestion = `"${suggestion.value.substring(0, 100)}${suggestion.value.length > 100 ? '...' : ''}"`;
      const noteContent = wasUsed
        ? `${t('AI_SUGGESTION.PRIVATE_NOTE_USED')} ${truncatedSuggestion}`
        : `${t('AI_SUGGESTION.PRIVATE_NOTE_NOT_USED')} ${truncatedSuggestion}`;

      try {
        await store.dispatch('createPendingMessageAndSend', {
          conversationId: props.conversationId,
          message: noteContent,
          private: true,
        });
      } catch (error) {
        console.error('Error logging suggestion usage:', error);
      }
    };

    // Get source label for display
    const sourceLabel = computed(() => {
      switch (suggestionSource.value) {
        case 'canned':
          return t('AI_SUGGESTION.SOURCE_CANNED');
        case 'aidexacx':
          return t('AI_SUGGESTION.SOURCE_AIDEXA_CX');
        case 'ai':
          return t('AI_SUGGESTION.SOURCE_AI');
        default:
          return '';
      }
    });

    // Watch for new incoming messages — invalidate cache and re-generate
    watch(
      () => lastIncomingMessage.value?.id,
      (newId, oldId) => {
        if (newId && newId !== oldId) {
          isDismissed.value = false;
          // New message arrived — invalidate cache and fetch fresh
          store.dispatch('aiSuggestionCache/invalidateSuggestion', {
            conversationId: props.conversationId,
          });
          generateSuggestion({ skipCache: true });
        }
      }
    );

    // Watch for conversation change — restore from cache instead of clearing
    watch(
      () => props.conversationId,
      () => {
        isDismissed.value = false;

        // Try to restore from cache for the new conversation
        const cached = getCachedSuggestion();
        if (
          cached &&
          lastIncomingMessage.value &&
          cached.lastMessageId === lastIncomingMessage.value.id
        ) {
          restoreFromCache(cached);
        } else {
          // No valid cache — clear and let onMounted/shouldShow logic handle re-fetch
          suggestion.value = '';
          suggestionSource.value = '';
          if (shouldShowSuggestion.value) {
            generateSuggestion();
          }
        }
      }
    );

    onMounted(() => {
      if (shouldShowSuggestion.value) {
        generateSuggestion();
      }
    });

    return {
      suggestion,
      suggestionSource,
      sourceLabel,
      isLoading,
      shouldShowSuggestion,
      useSuggestion,
      dismissSuggestion,
      t,
    };
  },
};
</script>

<template>
  <!-- Loading indicator - compact bar with text -->
  <div
    v-if="shouldShowSuggestion && isLoading"
    class="ai-loading-container flex items-center gap-2 mx-2 mb-2 px-3 py-2 bg-n-alpha-1 border border-n-strong rounded-lg"
  >
    <div class="ai-loading-spinner" />
    <span class="text-xs text-n-slate-11">{{
      t('AI_SUGGESTION.LOADING')
    }}</span>
  </div>

  <!-- Suggestion panel - only when suggestion is ready -->
  <div
    v-else-if="shouldShowSuggestion && suggestion"
    class="ai-suggestion-panel bg-n-alpha-1 border border-n-strong rounded-lg p-3 mb-2 mx-2"
  >
    <div class="flex items-start gap-3">
      <div class="flex-shrink-0">
        <Icon icon="i-lucide-sparkles" class="text-n-brand size-5" />
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 mb-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('AI_SUGGESTION.TITLE') }}
          </span>
          <span
            v-if="sourceLabel"
            class="text-xs px-2 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11"
          >
            {{ sourceLabel }}
          </span>
        </div>

        <p class="text-sm text-n-slate-12 whitespace-pre-wrap break-words mb-3">
          {{ suggestion }}
        </p>

        <div class="flex items-center gap-2">
          <NextButton
            size="sm"
            variant="solid"
            color="blue"
            icon="i-lucide-check"
            :label="t('AI_SUGGESTION.USE_BUTTON')"
            @click="useSuggestion"
          />

          <NextButton
            size="sm"
            variant="ghost"
            color="slate"
            icon="i-lucide-x"
            :label="t('AI_SUGGESTION.DISMISS_BUTTON')"
            @click="dismissSuggestion"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ai-suggestion-panel {
  animation: slideIn 0.2s ease-out;
}

.ai-loading-container {
  animation: slideIn 0.2s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ai-loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--n-alpha-3);
  border-top-color: var(--n-brand);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
